(() => {
  const STORAGE_SUBMITS = 'norphe_contact_submits';
  const STORAGE_SESSION = 'norphe_contact_session_activity';
  const STORAGE_PENDING = 'norphe_contact_pending_submit';
  const STORAGE_EXPECT_SENT = 'norphe_contact_expect_sent';
  const RATE_LIMIT_WINDOW_MS = 15 * 60 * 1000;
  const SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000;
  const STATUS_PARAM = 'contact_status';
  const RESET_PARAM = 'contact_reset';
  const VALID_STATUSES = new Set(['sent', 'technical_error', 'rate_limited', 'session_expired']);
  const CSRF_STORAGE_KEY = 'norphe_contact_csrf_token';

  const section = document.querySelector('[data-contact-section]');
  if (!section) return;

  const params = new URLSearchParams(window.location.search);
  const currentStatus = params.get(STATUS_PARAM);
  const expectSent = sessionStorage.getItem(STORAGE_EXPECT_SENT) === '1';
  const hadContactFormHash = (window.location.hash || '').toLowerCase() === '#contactform';

  if (!(expectSent && currentStatus !== 'sent')) {
    section.removeAttribute('data-contact-resolving');
  }

  if (hadContactFormHash) {
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${window.location.search}`
    );
  }

  const focusStatusPanel = () => {
    const panel =
      section.querySelector('[data-contact-status]') || section.querySelector('[data-contact-success]');
    if (!panel || document.activeElement === panel) return;
    requestAnimationFrame(() => {
      panel.focus({ preventScroll: true });
    });
  };

  const applyUrlStatusFallback = (status = currentStatus) => {
    if (!status || !VALID_STATUSES.has(status)) return;
    if (document.querySelector('[data-contact-status], [data-contact-success]')) return;

    const template = document.getElementById(`contact-status-template-${status}`);
    if (!template?.content) return;

    document.getElementById('ContactForm')?.remove();
    section.querySelector('.contact-title')?.remove();
    section.querySelector('.contact .visually-hidden')?.remove();

    section.appendChild(template.content.cloneNode(true));
    focusStatusPanel();
  };

  applyUrlStatusFallback();
  focusStatusPanel();

  const form = document.getElementById('ContactForm');

  const getContactBaseUrl = () => {
    const path = section.dataset.contactPageUrl || window.location.pathname;
    return new URL(path, window.location.origin);
  };

  const redirectWithStatus = (status) => {
    const url = getContactBaseUrl();
    url.searchParams.set(STATUS_PARAM, status);
    window.location.replace(url.toString());
  };

  const clearPostedFlags = () => {
    sessionStorage.removeItem(STORAGE_PENDING);
    sessionStorage.removeItem(STORAGE_EXPECT_SENT);
  };

  const readSubmitTimestamps = () => {
    try {
      const raw = localStorage.getItem(STORAGE_SUBMITS);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const pruneSubmits = (timestamps, now = Date.now()) =>
    timestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);

  const writeSubmitTimestamps = (timestamps) => {
    localStorage.setItem(STORAGE_SUBMITS, JSON.stringify(timestamps));
  };

  const getRecentSubmitCount = () => pruneSubmits(readSubmitTimestamps()).length;

  const isRateLimited = () => getRecentSubmitCount() > 3;

  const recordSubmitAttempt = () => {
    const now = Date.now();
    const pruned = pruneSubmits(readSubmitTimestamps(), now);
    pruned.push(now);
    writeSubmitTimestamps(pruned);
    return pruned.length;
  };

  const touchSession = () => {
    sessionStorage.setItem(STORAGE_SESSION, String(Date.now()));
  };

  const isSessionExpired = () => {
    const last = Number(sessionStorage.getItem(STORAGE_SESSION));
    if (!last) return false;
    return Date.now() - last > SESSION_TIMEOUT_MS;
  };

  const resetContactSession = () => {
    sessionStorage.removeItem(STORAGE_SESSION);
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
    touchSession();
    window.contactFormSecurity?.resetCsrfToken?.();
  };

  if (params.get(RESET_PARAM) === '1') {
    resetContactSession();
    const cleanUrl = getContactBaseUrl();
    window.history.replaceState({}, '', cleanUrl.pathname);
  }

  if (currentStatus === 'sent' || document.querySelector('[data-contact-success]')) {
    clearPostedFlags();
    touchSession();
  }

  if (expectSent && currentStatus !== 'sent' && !document.querySelector('[data-contact-success]')) {
    redirectWithStatus('sent');
    return;
  }

  if (!currentStatus) {
    if (isSessionExpired()) {
      redirectWithStatus('session_expired');
      return;
    }
    touchSession();
    window.contactFormSecurity?.init?.(document.getElementById('ContactForm'));

    const pending = sessionStorage.getItem(STORAGE_PENDING);
    const returnedFromShopify =
      params.get('form_type') === 'contact' ||
      [...params.keys()].some((key) => key.startsWith('contact['));
    const hasSuccess = document.querySelector('[data-contact-success]');

    if (pending && returnedFromShopify && !hasSuccess) {
      clearPostedFlags();
      redirectWithStatus('technical_error');
      return;
    }

    if (pending && hadContactFormHash && !hasSuccess) {
      clearPostedFlags();
      const url = getContactBaseUrl();
      url.searchParams.set(STATUS_PARAM, 'sent');
      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
      applyUrlStatusFallback('sent');
      touchSession();
      return;
    }

    if (!pending) {
      if (isRateLimited()) {
        redirectWithStatus('rate_limited');
        return;
      }
    } else {
      sessionStorage.removeItem(STORAGE_PENDING);
    }
  } else if (currentStatus === 'session_expired') {
    resetContactSession();
  }

  const refreshSessionOnActivity = () => touchSession();
  ['mousedown', 'keydown', 'touchstart', 'input'].forEach((eventName) => {
    document.addEventListener(eventName, refreshSessionOnActivity, { passive: true });
  });

  if (!form) return;

  const isLocalThemeDev = () =>
    window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';

  const markPendingSubmit = () => {
    if (isLocalThemeDev()) return;
    sessionStorage.setItem(STORAGE_PENDING, String(Date.now()));
  };

  form.addEventListener(
    'click',
    (e) => {
      const submitter = e.target.closest?.('button[type="submit"], input[type="submit"]');
      if (!submitter || !form.contains(submitter)) return;
      if (window.contactFormSecurity?.blockSubmitIfInsecure?.(form, e)) return;
      if (isRateLimited()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        redirectWithStatus('rate_limited');
        return;
      }
      markPendingSubmit();
    },
    true
  );

  form.addEventListener(
    'submit',
    () => {
      markPendingSubmit();
      recordSubmitAttempt();
    },
    true
  );

  if (document.querySelector('[data-contact-success]')) {
    clearPostedFlags();
    touchSession();
  }
})();
